import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import LocalMallOutlinedIcon from "@mui/icons-material/LocalMallOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import { jwtDecode } from "jwt-decode";

import { SidebarContext } from "../Sidebar/SidebarProvider";
import { fetchExecutiveBriefing } from "../../api/Insights/InsightsApi";
import {
  pygHeroShellSx,
  pygToolbarControlSx,
  pygToolbarFieldSx,
} from "../PyGAnalitico/PygAnaliticoPageHeader";
import {
  EMPRESA_OPTIONS,
  MESES_OPTIONS,
  SEVERIDAD_META,
  resolveAlertLink,
} from "./insightsConstants";
import {
  formatearFechaCortaInsights as formatearFechaCorta,
  formatearImporteInsights as formatearImporte,
  formatearPctInsights as formatearPct,
  kpiCfTone,
  kpiPresupuestoTone,
  kpiResultadoTone,
  kpiVentaTone,
} from "../../utils/insightsFormatUtils";
import {
  buildExecutiveBriefingParams,
  getInsightsCashflowDate,
  getInsightsEmpresaLabel,
  getInsightsMesLabel,
  shouldShowInsightsRolPanels,
} from "../../utils/insightsBriefingUtils";

const ROL_OPTIONS = [
  { value: "ceo", label: "CEO", color: "primary", icon: BusinessCenterOutlinedIcon },
  { value: "cfo", label: "CFO", color: "secondary", icon: SavingsOutlinedIcon },
  { value: "ambos", label: "Ambos", color: "info", icon: InsightsOutlinedIcon },
];

const panelShellSx = (theme, accent) => ({
  p: { xs: 1.5, sm: 2 },
  borderRadius: 3,
  border: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.75),
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(160deg, ${alpha(accent, 0.14)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 48%)`
      : `linear-gradient(160deg, ${alpha(accent, 0.07)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 55%)`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 28px rgba(0,0,0,0.22)"
      : "0 10px 40px rgba(15,23,42,0.05)",
  height: "100%",
});

const KpiCard = ({ label, value, sub, tone = "neutral", icon: Icon, loading }) => {
  const theme = useTheme();
  const toneColor =
    tone === "good"
      ? theme.palette.success.main
      : tone === "bad"
        ? theme.palette.error.main
        : tone === "warn"
          ? theme.palette.warning.main
          : theme.palette.primary.main;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        height: "100%",
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: alpha(toneColor, 0.22),
        bgcolor: alpha(theme.palette.background.paper, 0.92),
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 12px 28px ${alpha(toneColor, 0.12)}`,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: toneColor,
          borderRadius: "4px 0 0 4px",
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        {Icon ? (
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(toneColor, 0.1),
              color: toneColor,
            }}
          >
            <Icon sx={{ fontSize: 18 }} />
          </Box>
        ) : null}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase" }}
          >
            {label}
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, color: toneColor, lineHeight: 1.15, mt: 0.25 }}
          >
            {loading ? "…" : value}
          </Typography>
          {sub ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
              {sub}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
};

const MetricRow = ({ label, value, highlight }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    sx={{
      py: 0.75,
      px: 1,
      borderRadius: 1.5,
      bgcolor: highlight ? (t) => alpha(t.palette.primary.main, 0.06) : "transparent",
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 800 }}>
      {value}
    </Typography>
  </Stack>
);

const AlertRow = ({ alerta, empresa, expanded, onToggle, onNavigate }) => {
  const theme = useTheme();
  const meta = SEVERIDAD_META[alerta.severidad] || SEVERIDAD_META.media;
  const accent =
    meta.color === "error"
      ? theme.palette.error.main
      : meta.color === "warning"
        ? theme.palette.warning.main
        : meta.color === "info"
          ? theme.palette.info.main
          : theme.palette.grey[500];

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: alpha(accent, 0.28),
        borderRadius: 2.5,
        p: 1.25,
        bgcolor: alpha(theme.palette.background.paper, 0.96),
        borderLeft: `4px solid ${accent}`,
        transition: "box-shadow 0.2s ease",
        "&:hover": {
          boxShadow: `0 8px 24px ${alpha(accent, 0.1)}`,
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Chip
          size="small"
          label={meta.label}
          color={meta.color}
          sx={{ fontWeight: 700, minWidth: 72, height: 24 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
            {alerta.titulo}
          </Typography>
          <Stack direction="row" spacing={0.75} useFlexGap sx={{ mt: 0.5, flexWrap: "wrap" }}>
            {(alerta.rol || []).map((r) => (
              <Chip
                key={r}
                size="small"
                variant="outlined"
                label={r}
                sx={{
                  height: 22,
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  borderColor: alpha(theme.palette.divider, 0.8),
                }}
              />
            ))}
            {alerta.valor != null ? (
              <Chip
                size="small"
                label={
                  typeof alerta.valor === "number"
                    ? `${formatearImporte(alerta.valor)} €`
                    : alerta.valor
                }
                sx={{
                  height: 22,
                  fontWeight: 700,
                  bgcolor: alpha(accent, 0.08),
                  color: accent,
                }}
              />
            ) : null}
          </Stack>
        </Box>
        <Stack direction="row" spacing={0.5}>
          {alerta.enlace ? (
            <Tooltip title="Ir al detalle">
              <IconButton
                size="small"
                onClick={() => onNavigate(resolveAlertLink(alerta, empresa))}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.16) },
                }}
              >
                <OpenInNewOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          <IconButton size="small" onClick={onToggle}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>
      </Stack>
      <Collapse in={expanded}>
        <Box
          sx={{
            mt: 1,
            p: 1,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.action.hover, 0.4),
          }}
        >
          {alerta.evidencia && Object.keys(alerta.evidencia).length > 0 ? (
            <Typography
              component="pre"
              variant="caption"
              sx={{
                m: 0,
                whiteSpace: "pre-wrap",
                fontFamily: "ui-monospace, monospace",
                color: "text.secondary",
                fontSize: "0.7rem",
              }}
            >
              {JSON.stringify(alerta.evidencia, null, 2)}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Fuente: {alerta.fuente}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const SectionHeader = ({ icon: Icon, title, subtitle, accent, action }) => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={1}
      sx={{ mb: 1.5 }}
    >
      <Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {Icon ? <Icon sx={{ color: accent, fontSize: 22 }} /> : null}
          <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: -0.2 }}>
            {title}
          </Typography>
        </Stack>
        {subtitle ? (
          <Chip
            size="small"
            variant="outlined"
            label={subtitle}
            sx={{
              mt: 0.75,
              height: 22,
              fontWeight: 600,
              fontSize: "0.75rem",
              borderColor: alpha(accent, 0.35),
              bgcolor: alpha(accent, 0.04),
            }}
          />
        ) : null}
      </Box>
      {action}
    </Stack>
);

const InsightsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const { empresa: empresaParam } = useParams();

  const [empresa, setEmpresa] = useState((empresaParam || "stage").toLowerCase());
  const [year, setYear] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [rol, setRol] = useState("ambos");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [briefing, setBriefing] = useState(null);
  const [expandedAlerts, setExpandedAlerts] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp < Date.now() / 1000) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (empresaParam) setEmpresa(empresaParam.toLowerCase());
  }, [empresaParam]);

  const loadBriefing = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchExecutiveBriefing(
        buildExecutiveBriefingParams({ year, empresa, mes, rol }),
      );
      setBriefing(data);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Error cargando insights");
      setBriefing(null);
    } finally {
      setLoading(false);
    }
  }, [year, empresa, mes, rol]);

  useEffect(() => {
    loadBriefing();
  }, [loadBriefing]);

  const onEmpresaChange = (next) => {
    setEmpresa(next);
    navigate(next === "stage" ? "/insights" : `/insights/${encodeURIComponent(next)}`);
  };

  const resumen = briefing?.resumen_ejecutivo || {};
  const contadores = briefing?.contadores_alertas || {};
  const alertas = briefing?.alertas || [];
  const kpisCeo = briefing?.kpis_ceo || {};
  const kpisCfo = briefing?.kpis_cfo || {};

  const tituloEmpresa = getInsightsEmpresaLabel(empresa);

  const mesLabel = getInsightsMesLabel(mes);

  const kpiCards = useMemo(() => {
    const resumen = briefing?.resumen_ejecutivo || {};
    const cfFecha = getInsightsCashflowDate(briefing);
    const ventaTone = kpiVentaTone(resumen.venta_ytd_vs_ly_pct);
    const cfTone = kpiCfTone(resumen.cf_acumulado_hoy);
    const pptoTone = kpiPresupuestoTone(resumen.desviacion_vs_presupuesto_pct);

    return [
      {
        label: "Venta YTD",
        value: `${formatearImporte(resumen.venta_ytd)} €`,
        sub: `vs LY ${formatearPct(resumen.venta_ytd_vs_ly_pct)}`,
        tone: ventaTone,
        icon: ShowChartOutlinedIcon,
      },
      {
        label: "Resultado del ejercicio",
        value: `${formatearImporte(resumen.resultado_ejercicio_ytd ?? resumen.resultado_final_ytd)} €`,
        sub: `Explotación ${formatearImporte(resumen.resultado_explotacion_ytd)} €`,
        tone: kpiResultadoTone(
          resumen.resultado_ejercicio_ytd ?? resumen.resultado_final_ytd,
        ),
        icon: AccountBalanceOutlinedIcon,
      },
      {
        label: "CF acumulado",
        value: `${formatearImporte(resumen.cf_acumulado_hoy)} €`,
        sub: `al ${formatearFechaCorta(cfFecha)} · Runway ${resumen.runway_dias_estimado ?? "—"} d`,
        tone: cfTone,
        icon: WaterDropOutlinedIcon,
      },
      {
        label: "Saldo disponible",
        value: `${formatearImporte(resumen.saldo_liquido_disponible)} €`,
        sub: "572 + Revolut + USD",
        tone: "neutral",
        icon: AccountBalanceWalletOutlinedIcon,
      },
      {
        label: "vs Presupuesto",
        value: formatearPct(resumen.desviacion_vs_presupuesto_pct),
        sub: "Desviación YTD",
        tone: pptoTone,
        icon: FlagOutlinedIcon,
      },
    ];
  }, [briefing]);

  const pygPipelineCards = useMemo(() => {
    const resumen = briefing?.resumen_ejecutivo || {};
    const pipeline = briefing?.pyg_analitico?.pipeline || {};
    const alcance = pipeline.alcance_meses || "1-12";
    const yearLabel = briefing?.meta?.periodo?.year || year;

    return [
      {
        label: "Proyección ventas",
        value: `${formatearImporte(resumen.proyeccion_ventas ?? pipeline.proyeccion_ventas)} €`,
        sub: `PyG analítico · meses ${alcance} · ${yearLabel}`,
        tone: (resumen.proyeccion_ventas ?? pipeline.proyeccion_ventas) >= 0 ? "good" : "bad",
        icon: TimelineOutlinedIcon,
      },
      {
        label: "Proyección compras",
        value: `${formatearImporte(resumen.proyeccion_compras ?? pipeline.proyeccion_compras)} €`,
        sub: `PyG analítico · meses ${alcance} · ${yearLabel}`,
        tone: "neutral",
        icon: LocalMallOutlinedIcon,
      },
      {
        label: "Pedidos venta",
        value: `${formatearImporte(resumen.pedidos_venta ?? pipeline.pedidos_venta)} €`,
        sub: `Pipeline comercial · ${yearLabel}`,
        tone: "good",
        icon: ShoppingCartOutlinedIcon,
      },
      {
        label: "Pedidos compra",
        value: `${formatearImporte(resumen.pedidos_compra ?? pipeline.pedidos_compra)} €`,
        sub: `Compromisos compra · ${yearLabel}`,
        tone: "neutral",
        icon: ReceiptLongOutlinedIcon,
      },
      {
        label: "Resultado final",
        value: `${formatearImporte(
          (resumen.resultado_ejercicio_ytd ?? resumen.resultado_final_ytd ?? 0) +
            (resumen.proyeccion_ventas ?? pipeline.proyeccion_ventas ?? 0) +
            (resumen.proyeccion_compras ?? pipeline.proyeccion_compras ?? 0) +
            (resumen.pedidos_venta ?? pipeline.pedidos_venta ?? 0) +
            (resumen.pedidos_compra ?? pipeline.pedidos_compra ?? 0),
        )} €`,
        sub: "Resultado ejercicio + proy. + pedidos",
        tone: kpiResultadoTone(
          (resumen.resultado_ejercicio_ytd ?? resumen.resultado_final_ytd ?? 0) +
            (resumen.proyeccion_ventas ?? pipeline.proyeccion_ventas ?? 0) +
            (resumen.proyeccion_compras ?? pipeline.proyeccion_compras ?? 0) +
            (resumen.pedidos_venta ?? pipeline.pedidos_venta ?? 0) +
            (resumen.pedidos_compra ?? pipeline.pedidos_compra ?? 0),
        ),
        icon: SummarizeOutlinedIcon,
      },
    ];
  }, [briefing, year]);

  const { showCeo, showCfo } = shouldShowInsightsRolPanels(rol);
  const primaryAccent = theme.palette.primary.main;
  const ceoAccent = theme.palette.primary.main;
  const cfoAccent = theme.palette.secondary.main;

  return (
    <Box sx={{ p: { xs: 1.25, sm: 2 }, ...contentMargin, pb: 3 }}>
      <Box sx={pygHeroShellSx(theme, { sticky: true })}>
        <Box sx={{ p: { xs: 1.25, sm: 1.75 } }}>
          <Stack spacing={1.25}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={1.5}
              alignItems={{ lg: "flex-start" }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ flex: 1 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(primaryAccent, 0.16),
                    color: theme.palette.primary.dark,
                    border: "1px solid",
                    borderColor: alpha(primaryAccent, 0.28),
                    boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.5)}`,
                  }}
                >
                  <InsightsOutlinedIcon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ fontWeight: 700, letterSpacing: 1.1 }}
                  >
                    FINANCE · INSIGHTS EJECUTIVOS
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                    Insights · {tituloEmpresa}
                  </Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                    <Chip
                      size="small"
                      label={`${mesLabel} ${year}`}
                      sx={{ fontWeight: 700, bgcolor: alpha(primaryAccent, 0.08) }}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        briefing?.meta?.generatedAt
                          ? `Actualizado ${new Date(briefing.meta.generatedAt).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}`
                          : "Cargando…"
                      }
                      sx={{ fontWeight: 600 }}
                    />
                    {briefing?.meta?.periodo?.corte ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Corte PyG/CF ${formatearFechaCorta(briefing.meta.periodo.corte)}`}
                        sx={{ fontWeight: 600 }}
                      />
                    ) : null}
                  </Stack>
                </Box>
              </Stack>

              <Paper
                elevation={0}
                sx={{
                  p: 1.25,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.divider, 0.7),
                  bgcolor: alpha(theme.palette.background.paper, 0.72),
                  width: { xs: "100%", lg: "auto" },
                  minWidth: { lg: 420 },
                }}
              >
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 120, ...pygToolbarControlSx }}>
                      <InputLabel>Empresa</InputLabel>
                      <Select
                        label="Empresa"
                        value={empresa}
                        onChange={(e) => onEmpresaChange(e.target.value)}
                        sx={pygToolbarFieldSx(theme)}
                      >
                        {EMPRESA_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 100, ...pygToolbarControlSx }}>
                      <InputLabel>Año</InputLabel>
                      <Select
                        label="Año"
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                        sx={pygToolbarFieldSx(theme)}
                      >
                        {[year - 1, year, year + 1].map((y) => (
                          <MenuItem key={y} value={y}>
                            {y}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 90, ...pygToolbarControlSx }}>
                      <InputLabel>Mes</InputLabel>
                      <Select
                        label="Mes"
                        value={mes}
                        onChange={(e) => setMes(Number(e.target.value))}
                        sx={pygToolbarFieldSx(theme)}
                      >
                        {MESES_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={loading ? <CircularProgress size={14} /> : <RefreshOutlinedIcon />}
                      onClick={loadBriefing}
                      disabled={loading}
                      sx={{
                        height: 40,
                        fontWeight: 700,
                        borderRadius: 1.5,
                        borderColor: alpha(primaryAccent, 0.4),
                      }}
                    >
                      Actualizar
                    </Button>
                  </Stack>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mr: 0.25 }}>
                      Vista
                    </Typography>
                    {ROL_OPTIONS.map((opt) => {
                      const selected = rol === opt.value;
                      const main = theme.palette[opt.color].main;
                      return (
                        <Chip
                          key={opt.value}
                          icon={<opt.icon sx={{ fontSize: "16px !important" }} />}
                          label={opt.label}
                          clickable
                          onClick={() => setRol(opt.value)}
                          variant={selected ? "filled" : "outlined"}
                          color={opt.color}
                          sx={{
                            fontWeight: 700,
                            height: 30,
                            transition: "all 0.2s ease",
                            ...(selected
                              ? { boxShadow: `0 4px 12px ${alpha(main, 0.35)}` }
                              : {
                                  bgcolor: alpha(main, 0.06),
                                  borderColor: alpha(main, 0.3),
                                  color: main,
                                }),
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Stack>
              </Paper>
            </Stack>

            <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
              <Chip
                size="small"
                color="error"
                label={`${contadores.critica || 0} críticas`}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                color="warning"
                label={`${contadores.alta || 0} altas`}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                color="info"
                label={`${contadores.media || 0} medias`}
                sx={{ fontWeight: 700 }}
              />
              {resumen.runway_dias_estimado != null ? (
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<AccountBalanceWalletOutlinedIcon />}
                  label={`Runway ${resumen.runway_dias_estimado} días`}
                  sx={{ fontWeight: 700, borderColor: alpha(cfoAccent, 0.45) }}
                />
              ) : null}
              <Chip
                size="small"
                variant="outlined"
                label={`${alertas.length} alertas visibles`}
                sx={{ fontWeight: 600 }}
              />
            </Stack>
          </Stack>
        </Box>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2.5 }}>
          {error}
        </Alert>
      ) : null}

      {briefing?.meta?.warnings?.length > 0 ? (
        <Stack spacing={0.75} sx={{ mt: 1.5 }}>
          {briefing.meta.warnings.map((w) => (
            <Alert key={w} severity="warning" sx={{ borderRadius: 2.5 }}>
              {w}
            </Alert>
          ))}
        </Stack>
      ) : null}

      <Box
        sx={{
          mt: 1.5,
          display: "grid",
          gap: 1.25,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(5, 1fr)",
          },
        }}
      >
        {kpiCards.map((kpi) => (
          <KpiCard key={kpi.label} {...kpi} loading={loading} />
        ))}
      </Box>

      <Box sx={{ mt: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            PyG Analítico · Pipeline
          </Typography>
          <Button
            size="small"
            variant="text"
            endIcon={<OpenInNewOutlinedIcon />}
            onClick={() =>
              navigate(`/pyg-analitico/${empresa === "stage" ? "fusion" : empresa}`)
            }
          >
            Ver tabla PyG
          </Button>
        </Stack>
        <Box
          sx={{
            display: "grid",
            gap: 1.25,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(5, 1fr)",
            },
          }}
        >
          {pygPipelineCards.map((kpi) => (
            <KpiCard key={kpi.label} {...kpi} loading={loading} />
          ))}
        </Box>
      </Box>

      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
        <Grid item xs={12} lg={7}>
          <Paper elevation={0} sx={panelShellSx(theme, theme.palette.error.main)}>
            <SectionHeader
              icon={NotificationsActiveOutlinedIcon}
              title="Alertas priorizadas"
              subtitle={`${alertas.length} señales · ordenadas por severidad`}
              accent={theme.palette.error.main}
            />
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : alertas.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No hay alertas para los filtros seleccionados.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {alertas.map((alerta) => (
                  <AlertRow
                    key={`${alerta.id}-${alerta.marca || ""}-${alerta.titulo}`}
                    alerta={alerta}
                    empresa={empresa}
                    expanded={!!expandedAlerts[alerta.id + (alerta.marca || "")]}
                    onToggle={() =>
                      setExpandedAlerts((prev) => ({
                        ...prev,
                        [alerta.id + (alerta.marca || "")]:
                          !prev[alerta.id + (alerta.marca || "")],
                      }))
                    }
                    onNavigate={navigate}
                  />
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Stack spacing={1.5}>
            {showCeo ? (
              <Paper elevation={0} sx={panelShellSx(theme, ceoAccent)}>
                <SectionHeader
                  icon={TrendingUpOutlinedIcon}
                  title="Negocio (CEO)"
                  subtitle="Marcas y pipeline"
                  accent={ceoAccent}
                />
                <Stack spacing={0.25}>
                  <MetricRow
                    label="Marcas interesantes"
                    value={kpisCeo.marcas_interesantes ?? 0}
                    highlight
                  />
                  <MetricRow label="Marcas en riesgo" value={kpisCeo.marcas_riesgo ?? 0} />
                  <MetricRow
                    label="Pipeline / venta"
                    value={formatearPct(kpisCeo.pipeline_vs_venta_pct)}
                  />
                </Stack>
                <Chip
                  clickable
                  size="small"
                  icon={<OpenInNewOutlinedIcon />}
                  label="Ver analítica por marca"
                  onClick={() => navigate(`/pyg-analitico/${empresa}/por-marca`)}
                  sx={{
                    mt: 1.25,
                    fontWeight: 700,
                    bgcolor: alpha(ceoAccent, 0.1),
                    "&:hover": { bgcolor: alpha(ceoAccent, 0.18) },
                  }}
                />
              </Paper>
            ) : null}

            {showCfo ? (
              <Paper elevation={0} sx={panelShellSx(theme, cfoAccent)}>
                <SectionHeader
                  icon={TrendingDownOutlinedIcon}
                  title="Tesorería (CFO)"
                  subtitle="Liquidez y obligaciones"
                  accent={cfoAccent}
                />
                <Stack spacing={0.25}>
                  <MetricRow
                    label="Gap 30d"
                    value={`${formatearImporte(kpisCfo.gap_30d)} €`}
                    highlight
                  />
                  <MetricRow
                    label="IVA próximo"
                    value={`${formatearImporte(kpisCfo.iva_proximo_pago)} €`}
                  />
                  <MetricRow
                    label="Nómina YTD"
                    value={`${formatearImporte(kpisCfo.nomina_gasto_ytd)} €`}
                  />
                  <MetricRow
                    label="Líneas crédito"
                    value={formatearPct(kpisCfo.lineas_credito_uso_pct)}
                  />
                </Stack>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                  <Chip
                    clickable
                    size="small"
                    icon={<OpenInNewOutlinedIcon />}
                    label="Cash Flow"
                    onClick={() =>
                      navigate(empresa === "stage" ? "/cashflow" : `/cashflow/${empresa}`)
                    }
                    sx={{
                      fontWeight: 700,
                      bgcolor: alpha(cfoAccent, 0.1),
                      "&:hover": { bgcolor: alpha(cfoAccent, 0.18) },
                    }}
                  />
                  <Chip
                    clickable
                    size="small"
                    icon={<OpenInNewOutlinedIcon />}
                    label="Nóminas"
                    onClick={() => navigate("/nominas")}
                    sx={{
                      fontWeight: 700,
                      bgcolor: alpha(cfoAccent, 0.1),
                      "&:hover": { bgcolor: alpha(cfoAccent, 0.18) },
                    }}
                  />
                </Stack>
              </Paper>
            ) : null}

            <Paper
              elevation={0}
              sx={{
                ...panelShellSx(theme, theme.palette.info.main),
                background:
                  theme.palette.mode === "dark"
                    ? `linear-gradient(145deg, ${alpha(theme.palette.info.main, 0.18)} 0%, ${alpha(theme.palette.secondary.main, 0.12)} 50%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`
                    : `linear-gradient(145deg, ${alpha("#e8f4ff", 0.9)} 0%, ${alpha("#f3e8ff", 0.7)} 55%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
              }}
            >
              <SectionHeader
                icon={AutoAwesomeOutlinedIcon}
                title="Narrativa IA"
                subtitle="Fase 2 · resumen ejecutivo"
                accent={theme.palette.info.main}
              />
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                Resumen generado por IA sobre este briefing (CEO / CFO). Los KPIs y alertas de
                arriba ya son deterministas y auditables.
              </Typography>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InsightsPage;
